import {
  Box,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { ActionIconButton } from "../../ActionIconButton";

// ------------------------------------------------------------------
// Paginação padrão das telas Iugu.
// - Com totalItems conhecido: paginação numerada + "Exibindo X–Y de Z"
// - Sem totalItems (API não informa): versão compacta anterior/próxima
// ------------------------------------------------------------------

type IuguPaginationProps = {
  page: number;
  setPage: (page: number) => void;
  list: any[];
  totalItems?: number | null;
  perPage?: number;
  setPerPage?: (perPage: number) => void;
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];

export const IuguPagination = ({
  page,
  setPage,
  list,
  totalItems = null,
  perPage = 10,
  setPerPage,
}: IuguPaginationProps) => {
  const hasTotal = typeof totalItems === "number" && totalItems >= 0;
  const pageCount = hasTotal ? Math.max(1, Math.ceil(totalItems / perPage)) : null;

  const firstShown = totalItems === 0 ? 0 : (page - 1) * perPage + 1;
  const lastShown = (page - 1) * perPage + list.length;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1.5,
        mt: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {hasTotal
            ? `Exibindo ${firstShown.toLocaleString("pt-BR")}–${lastShown.toLocaleString(
                "pt-BR"
              )} de ${totalItems!.toLocaleString("pt-BR")}`
            : `Página ${page}`}
        </Typography>
        {setPerPage && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Linhas por página:
            </Typography>
            <Select
              size="small"
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
              sx={{ fontSize: "0.85rem", height: 32 }}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </Box>

      {pageCount !== null ? (
        <Pagination
          count={pageCount}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
          shape="rounded"
          showFirstButton
          showLastButton
          siblingCount={1}
        />
      ) : (
        <Box sx={{ display: "flex", gap: 1 }}>
          <ActionIconButton
            title="Página anterior"
            icon={<FaArrowLeft size={14} />}
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          />
          <ActionIconButton
            title="Próxima página"
            icon={<FaArrowRight size={14} />}
            disabled={list.length < perPage}
            onClick={() => setPage(page + 1)}
          />
        </Box>
      )}
    </Box>
  );
};
